/**
 * RackDetailView - Cross-section view of a rack showing shelves and products
 */

import React, { useEffect, useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Chip,
  Tooltip,
  CircularProgress,
  IconButton,
  Paper,
  Divider,
  TextField,
  Collapse,
  Stack,
} from '@mui/material';
import {
  Close as CloseIcon,
  Inventory2 as InventoryIcon,
  Warning as WarningIcon,
  Height as HeightIcon,
  Add as AddIcon,
  Delete as DeleteIcon,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';

import { getRackDetail, createShelf, deleteShelf as deleteShelfApi } from '../../services/locationApi';
import {
  RackDetail,
  ShelfInfo,
  ShelfSlot,
  ShelfProduct,
  HeightAccessibility,
  getHeightAccessibilityLabel,
  getHeightAccessibilityColor,
} from './pathTypes';

interface RackDetailViewProps {
  open: boolean;
  onClose: () => void;
  locationId: number | null;
  highlightedProductId?: number;
  highlightedShelfLevel?: number;
  onProductClick?: (product: ShelfProduct, location: { shelfLevel: number; slotIndex: number }) => void;
  editable?: boolean;
  onShelfChanged?: () => void;
}

const RackDetailView: React.FC<RackDetailViewProps> = ({
  open,
  onClose,
  locationId,
  highlightedProductId,
  highlightedShelfLevel,
  onProductClick,
  editable = false,
  onShelfChanged,
}) => {
  const { t } = useTranslation();
  const [rack, setRack] = useState<RackDetail | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showAddShelf, setShowAddShelf] = useState(false);
  const [addShelfForm, setAddShelfForm] = useState({ level: 1, heightCm: 50, slots: 3 });
  const [addingShelf, setAddingShelf] = useState(false);
  const [deletingShelf, setDeletingShelf] = useState<number | null>(null);

  useEffect(() => {
    if (open && locationId) {
      loadRackDetail();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- loadRackDetail defined below
  }, [open, locationId]);

  const loadRackDetail = async () => {
    if (!locationId) return;

    setLoading(true);
    setError(null);

    try {
      const data = await getRackDetail(locationId);
      setRack(data);
    } catch (err: any) {
      console.error('Error loading rack detail:', err);
      setError(err.response?.data?.error || 'Nie udało się załadować szczegółów regału');
    } finally {
      setLoading(false);
    }
  };

  const handleAddShelf = async () => {
    if (!locationId || !rack) return;
    setAddingShelf(true);
    try {
      await createShelf(locationId, rack.id, {
        shelfLevel: addShelfForm.level,
        heightCm: addShelfForm.heightCm,
        slotsCount: addShelfForm.slots,
      });
      await loadRackDetail();
      onShelfChanged?.();
      setShowAddShelf(false);
      setAddShelfForm({ level: (rack.shelves.length > 0 ? Math.max(...rack.shelves.map(s => s.level)) + 1 : 1), heightCm: 50, slots: 3 });
    } catch (err: any) {
      console.error('Failed to add shelf:', err);
      setError(err.response?.data?.error || t('warehouseMap.rackDetail.addShelfError'));
    } finally {
      setAddingShelf(false);
    }
  };

  const handleDeleteShelf = async (shelf: ShelfInfo) => {
    if (!locationId) return;
    setDeletingShelf(shelf.level);
    try {
      const slotIds = shelf.slots.map(s => s.locationId).filter((id): id is number => id != null);
      if (slotIds.length > 0) {
        await deleteShelfApi(slotIds);
      }
      await loadRackDetail();
      onShelfChanged?.();
    } catch (err: any) {
      console.error('Failed to delete shelf:', err);
      setError(err.response?.data?.error || t('warehouseMap.rackDetail.deleteShelfError'));
    } finally {
      setDeletingShelf(null);
    }
  };

  const getShelfBackgroundColor = (shelf: ShelfInfo, isHighlighted: boolean): string => {
    if (isHighlighted) {
      return 'rgba(33, 150, 243, 0.2)';
    }
    const baseColor = getHeightAccessibilityColor(shelf.heightAccessibility);
    return `${baseColor}20`; // 20 = 12% opacity in hex
  };

  const renderProduct = (product: ShelfProduct, shelfLevel: number, slotIndex: number) => {
    const isHighlighted = highlightedProductId === product.productId;
    const hasExpiringSoon = product.expiryDate &&
      new Date(product.expiryDate) < new Date(Date.now() + 30 * 24 * 60 * 60 * 1000);

    return (
      <Tooltip
        key={product.stockId}
        title={
          <Box>
            <Typography variant="body2" fontWeight="bold">{product.productName}</Typography>
            <Typography variant="caption">Kod: {product.productCode}</Typography>
            <br />
            <Typography variant="caption">Ilość: {product.quantity} (dostępne: {product.availableQuantity})</Typography>
            {product.batchNumber && (
              <>
                <br />
                <Typography variant="caption">Partia: {product.batchNumber}</Typography>
              </>
            )}
            {product.expiryDate && (
              <>
                <br />
                <Typography variant="caption" color={hasExpiringSoon ? 'error' : 'inherit'}>
                  Ważność: {new Date(product.expiryDate).toLocaleDateString('pl-PL')}
                </Typography>
              </>
            )}
          </Box>
        }
        arrow
      >
        <Chip
          icon={hasExpiringSoon ? <WarningIcon /> : <InventoryIcon />}
          label={`${product.productCode} (${product.quantity})`}
          size="small"
          color={isHighlighted ? 'primary' : hasExpiringSoon ? 'warning' : 'default'}
          variant={isHighlighted ? 'filled' : 'outlined'}
          onClick={() => onProductClick?.(product, { shelfLevel, slotIndex })}
          sx={{
            cursor: onProductClick ? 'pointer' : 'default',
            width: '100%',
            maxWidth: '100%',
            justifyContent: 'flex-start',
            '& .MuiChip-icon': {
              ml: 0.5,
              mr: 0.25,
            },
            '& .MuiChip-label': {
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              width: '100%',
              textAlign: 'left',
            },
          }}
        />
      </Tooltip>
    );
  };

  const renderSlot = (slot: ShelfSlot, shelf: ShelfInfo) => {
    const hasProducts = slot.products.length > 0;

    return (
      <Box
        key={slot.slotIndex}
        sx={{
          flex: 1,
          minWidth: 150,
          maxWidth: 260,
          p: 0.75,
          borderRight: '1px solid',
          borderColor: 'divider',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'stretch',
          justifyContent: 'flex-start',
          minHeight: 88,
          bgcolor: hasProducts ? 'action.hover' : 'transparent',
          '&:last-child': {
            borderRight: 'none',
          },
        }}
      >
        <Typography variant="caption" color="text.secondary" sx={{ mb: 0.5 }}>
          S{slot.slotIndex}
        </Typography>
        <Stack
          spacing={0.5}
          sx={{
            width: '100%',
            maxHeight: 120,
            overflowY: 'auto',
            pr: 0.25,
          }}
        >
          {slot.products.map((product) =>
            renderProduct(product, shelf.level, slot.slotIndex)
          )}
        </Stack>
      </Box>
    );
  };

  const renderShelf = (shelf: ShelfInfo) => {
    const isHighlighted = highlightedShelfLevel === shelf.level;
    const totalProducts = shelf.slots.reduce((sum, slot) =>
      sum + slot.products.reduce((s, p) => s + p.quantity, 0), 0
    );

    return (
      <Paper
        key={shelf.level}
        elevation={isHighlighted ? 3 : 1}
        sx={{
          mb: 1,
          overflow: 'hidden',
          bgcolor: getShelfBackgroundColor(shelf, isHighlighted),
          border: isHighlighted ? '2px solid' : '1px solid',
          borderColor: isHighlighted ? 'primary.main' : 'divider',
        }}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            px: 1,
            py: 0.5,
            bgcolor: 'action.selected',
            borderBottom: '1px solid',
            borderColor: 'divider',
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography variant="subtitle2" fontWeight="bold">
              Półka {shelf.level}
            </Typography>
            <Chip
              icon={<HeightIcon />}
              label={`${shelf.heightCm} cm`}
              size="small"
              variant="outlined"
            />
            <Tooltip title={getHeightAccessibilityLabel(shelf.heightAccessibility)}>
              <Box
                sx={{
                  width: 12,
                  height: 12,
                  borderRadius: '50%',
                  bgcolor: getHeightAccessibilityColor(shelf.heightAccessibility),
                }}
              />
            </Tooltip>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <Typography variant="caption" color="text.secondary">
              {totalProducts} szt.
            </Typography>
            {editable && (
              <Tooltip title={t('warehouseMap.rackDetail.deleteShelf')}>
                <span>
                  <IconButton
                    size="small"
                    color="error"
                    onClick={(e) => { e.stopPropagation(); handleDeleteShelf(shelf); }}
                    disabled={deletingShelf === shelf.level || totalProducts > 0}
                    sx={{ p: 0.25 }}
                  >
                    {deletingShelf === shelf.level ? <CircularProgress size={14} /> : <DeleteIcon sx={{ fontSize: 16 }} />}
                  </IconButton>
                </span>
              </Tooltip>
            )}
          </Box>
        </Box>

        <Box sx={{ display: 'flex', overflowX: 'auto' }}>
          {shelf.slots.map((slot) => renderSlot(slot, shelf))}
          {shelf.slots.length === 0 && (
            <Typography variant="body2" color="text.secondary" sx={{ p: 2 }}>
              Brak slotów
            </Typography>
          )}
        </Box>
      </Paper>
    );
  };

  const renderContent = () => {
    if (loading) {
      return (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 200 }}>
          <CircularProgress />
        </Box>
      );
    }

    if (error) {
      return (
        <Box sx={{ textAlign: 'center', py: 4 }}>
          <Typography color="error">{error}</Typography>
          <Button onClick={loadRackDetail} sx={{ mt: 2 }}>
            Spróbuj ponownie
          </Button>
        </Box>
      );
    }

    if (!rack) {
      return (
        <Typography color="text.secondary" sx={{ textAlign: 'center', py: 4 }}>
          Brak danych
        </Typography>
      );
    }

    // Sort shelves by level (highest first for visual representation)
    const sortedShelves = [...rack.shelves].sort((a, b) => b.level - a.level);

    return (
      <Box>
        {/* Rack info header */}
        <Box sx={{ mb: 2 }}>
          <Typography variant="body2" color="text.secondary">
            Pozycja: ({rack.position.x}, {rack.position.y}) |
            Wymiary: {rack.width}x{rack.height}
            {rack.maxItems && ` | Max: ${rack.maxItems} szt.`}
          </Typography>
        </Box>

        {/* Legend */}
        <Box sx={{ display: 'flex', gap: 2, mb: 2, flexWrap: 'wrap' }}>
          <Typography variant="caption" fontWeight="bold">Dostępność:</Typography>
          {(['GROUND', 'EASY', 'NORMAL', 'LADDER', 'EQUIPMENT'] as HeightAccessibility[]).map((level) => (
            <Box key={level} sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <Box
                sx={{
                  width: 10,
                  height: 10,
                  borderRadius: '50%',
                  bgcolor: getHeightAccessibilityColor(level),
                }}
              />
              <Typography variant="caption">
                {getHeightAccessibilityLabel(level)}
              </Typography>
            </Box>
          ))}
        </Box>

        <Divider sx={{ mb: 2 }} />

        {/* Add shelf form */}
        {editable && (
          <Box sx={{ mb: 2 }}>
            <Button
              size="small"
              startIcon={<AddIcon />}
              onClick={() => setShowAddShelf(!showAddShelf)}
              variant={showAddShelf ? 'contained' : 'outlined'}
            >
              {t('warehouseMap.rackDetail.addShelf')}
            </Button>
            <Collapse in={showAddShelf}>
              <Paper variant="outlined" sx={{ mt: 1, p: 1.5, display: 'flex', gap: 1.5, alignItems: 'flex-end', flexWrap: 'wrap' }}>
                <TextField
                  size="small"
                  label={t('warehouseMap.rackDetail.shelfLevel')}
                  type="number"
                  value={addShelfForm.level}
                  onChange={(e) => setAddShelfForm(prev => ({ ...prev, level: Math.max(1, parseInt(e.target.value) || 1) }))}
                  sx={{ width: 100 }}
                  inputProps={{ min: 1 }}
                />
                <TextField
                  size="small"
                  label={t('warehouseMap.rackDetail.shelfHeight')}
                  type="number"
                  value={addShelfForm.heightCm}
                  onChange={(e) => setAddShelfForm(prev => ({ ...prev, heightCm: Math.max(1, parseInt(e.target.value) || 50) }))}
                  sx={{ width: 120 }}
                  inputProps={{ min: 1 }}
                />
                <TextField
                  size="small"
                  label={t('warehouseMap.rackDetail.slotsCount')}
                  type="number"
                  value={addShelfForm.slots}
                  onChange={(e) => setAddShelfForm(prev => ({ ...prev, slots: Math.max(1, parseInt(e.target.value) || 1) }))}
                  sx={{ width: 100 }}
                  inputProps={{ min: 1, max: 20 }}
                />
                <Button
                  size="small"
                  variant="contained"
                  onClick={handleAddShelf}
                  disabled={addingShelf}
                  startIcon={addingShelf ? <CircularProgress size={14} /> : <AddIcon />}
                >
                  {t('warehouseMap.rackDetail.confirm')}
                </Button>
              </Paper>
            </Collapse>
          </Box>
        )}

        {/* Shelves (from top to bottom) */}
        <Box sx={{
          maxHeight: 400,
          overflowY: 'auto',
          border: '2px solid',
          borderColor: 'divider',
          borderRadius: 1,
          p: 1,
          bgcolor: 'background.default',
        }}>
          {sortedShelves.length > 0 ? (
            sortedShelves.map(renderShelf)
          ) : (
            <Typography color="text.secondary" sx={{ textAlign: 'center', py: 4 }}>
              Ten regał nie ma zdefiniowanych półek
            </Typography>
          )}

          {/* Floor indicator */}
          <Box
            sx={{
              mt: 1,
              p: 1,
              bgcolor: 'grey.300',
              borderRadius: 1,
              textAlign: 'center',
            }}
          >
            <Typography variant="caption" color="text.secondary">
              PODŁOGA
            </Typography>
          </Box>
        </Box>
      </Box>
    );
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="xl"
      fullWidth
      BackdropProps={{
        sx: {
          backgroundColor: 'rgba(0, 0, 0, 0.6)',
        },
      }}
      PaperProps={{
        sx: {
          minHeight: 400,
          bgcolor: '#1f1f1f',
          backgroundImage: 'none',
          opacity: 1,
        }
      }}
    >
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', bgcolor: '#1f1f1f' }}>
        <Box>
          <Typography variant="h6" component="span">
            {rack ? `${rack.code} - ${rack.name}` : 'Przekrój regału'}
          </Typography>
          {rack && (
            <Chip
              label={rack.locationType}
              size="small"
              sx={{ ml: 1 }}
              color="primary"
              variant="outlined"
            />
          )}
        </Box>
        <IconButton
          onClick={onClose}
          size="small"
          sx={{
            color: '#fff',
            '&:hover': { color: '#fff' },
          }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers sx={{ bgcolor: '#1f1f1f' }}>
        {renderContent()}
      </DialogContent>

      <DialogActions sx={{ bgcolor: '#1f1f1f' }}>
        <Button onClick={onClose}>
          Zamknij
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default RackDetailView;
